import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import BarcodeScanner from "../components/BarcodeScanner";
import {
  ArrowUpFromLine,
  Loader2,
  Package,
  Plus,
  Trash2,
  CheckCircle,
  Barcode,
  ShoppingCart,
  AlertTriangle,
  User,
  Users,
  Search,
  X
} from "lucide-react";

const MOTIVOS_SALIDA = [
  { value: "venta", label: "Venta" },
  { value: "consumo", label: "Consumo interno" },
  { value: "devolucion_proveedor", label: "Devolución a proveedor" },
  { value: "ajuste_negativo", label: "Ajuste negativo" },
  { value: "merma", label: "Merma / Pérdida" },
  { value: "transferencia", label: "Transferencia salida" },
  { value: "donacion", label: "Donación" },
  { value: "otro", label: "Otro" }
];

export default function SalidasInventarioPage() {
  const [articulos, setArticulos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedArticulo, setSelectedArticulo] = useState(null);
  const [articuloSearch, setArticuloSearch] = useState("");
  const [showArticulosList, setShowArticulosList] = useState(false);
  const [items, setItems] = useState([]);
  const [referenciaMode, setReferenciaMode] = useState("usuario"); // "usuario" or "manual"
  const [formData, setFormData] = useState({
    cantidad: 1,
    motivo: "venta",
    referencia: "",
    referencia_usuario_id: "",
    observaciones: "",
    precio_unitario: 0
  });
  const { getAuthHeaders } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [articulosRes, usuariosRes] = await Promise.all([
        axios.get(`${API}/inventario/articulos?limit=1000`, getAuthHeaders()),
        axios.get(`${API}/users`, getAuthHeaders())
      ]);
      // Handle paginated response format
      const articulosData = articulosRes.data.items || articulosRes.data;
      setArticulos(Array.isArray(articulosData) ? articulosData.filter(a => a.activo !== false) : []);
      setUsuarios(usuariosRes.data || []);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBarcodeScanned = async (codigo) => {
    try {
      const response = await axios.get(`${API}/inventario/articulos/codigo/${codigo}`, getAuthHeaders());
      if (response.data) {
        setSelectedArticulo(response.data);
        setFormData(prev => ({ ...prev, precio_unitario: response.data.precio_venta || 0 }));
        toast.success(`Artículo encontrado: ${response.data.nombre}`);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error("Artículo no encontrado con ese código");
      }
    }
  };

  const handleSelectArticulo = (articuloId) => {
    const articulo = articulos.find(a => a.id === articuloId);
    if (articulo) {
      setSelectedArticulo(articulo);
      setFormData(prev => ({ ...prev, precio_unitario: articulo.precio_venta || 0 }));
      setArticuloSearch("");
      setShowArticulosList(false);
    }
  };

  // Filter articles based on search
  const filteredArticulos = articulos.filter(art => {
    if (!articuloSearch.trim()) return true;
    const search = articuloSearch.toLowerCase();
    return (
      art.codigo?.toLowerCase().includes(search) ||
      art.nombre?.toLowerCase().includes(search)
    );
  });

  const addToList = () => {
    if (!selectedArticulo) {
      toast.error("Selecciona un artículo");
      return;
    }
    if (formData.cantidad <= 0) {
      toast.error("La cantidad debe ser mayor a 0");
      return;
    }

    // Check stock
    const existingInList = items.filter(i => i.articulo_id === selectedArticulo.id).reduce((sum, i) => sum + i.cantidad, 0);
    const totalRequested = existingInList + formData.cantidad;
    
    if (totalRequested > selectedArticulo.stock_actual) {
      toast.error(`Stock insuficiente. Disponible: ${selectedArticulo.stock_actual - existingInList}`);
      return;
    }

    const existingIndex = items.findIndex(i => i.articulo_id === selectedArticulo.id);
    if (existingIndex >= 0) {
      // Update existing item
      const newItems = [...items];
      newItems[existingIndex].cantidad += formData.cantidad;
      setItems(newItems);
    } else {
      // Add new item
      setItems([...items, {
        articulo_id: selectedArticulo.id,
        articulo: selectedArticulo,
        tipo: "salida",
        cantidad: formData.cantidad,
        motivo: formData.motivo,
        referencia: formData.referencia,
        observaciones: formData.observaciones,
        precio_unitario: formData.precio_unitario
      }]);
    }

    // Reset form
    setSelectedArticulo(null);
    setFormData({
      cantidad: 1,
      motivo: formData.motivo,
      referencia: formData.referencia,
      observaciones: "",
      precio_unitario: 0
    });
  };

  const removeFromList = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (items.length === 0) {
      toast.error("Agrega al menos un artículo");
      return;
    }

    try {
      setSaving(true);
      // Clean items to only include fields expected by backend
      const cleanItems = items.map(item => ({
        articulo_id: item.articulo_id,
        tipo: item.tipo,
        cantidad: item.cantidad,
        motivo: item.motivo,
        referencia: item.referencia || "",
        observaciones: item.observaciones || "",
        precio_unitario: item.precio_unitario || 0
      }));
      
      const response = await axios.post(`${API}/inventario/movimientos/lote`, cleanItems, getAuthHeaders());
      
      if (response.data.errores > 0) {
        toast.warning(`${response.data.procesados} salidas registradas, ${response.data.errores} errores`);
        if (response.data.detalles_errores?.length > 0) {
          response.data.detalles_errores.forEach(err => {
            toast.error(`Error: ${err.error}`);
          });
        }
      } else {
        toast.success(`${response.data.procesados} salidas registradas exitosamente`);
      }
      
      setItems([]);
      fetchData();
    } catch (err) {
      console.error("Error al registrar salidas:", err);
      toast.error(err.response?.data?.detail || "Error al registrar salidas");
    } finally {
      setSaving(false);
    }
  };

  const totalItems = items.reduce((sum, i) => sum + i.cantidad, 0);
  const totalValue = items.reduce((sum, i) => sum + (i.cantidad * i.precio_unitario), 0);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="salidas-inventario-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
              <ArrowUpFromLine className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Salidas de Inventario
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Registrar egreso de artículos del inventario
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form Section */}
          <div className="lg:col-span-2 space-y-6">
            {/* Barcode Scanner */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Barcode className="w-5 h-5" />
                  Escanear Artículo
                </CardTitle>
                <CardDescription>
                  Escanea el código de barras o selecciona manualmente
                </CardDescription>
              </CardHeader>
              <CardContent>
                <BarcodeScanner onScan={handleBarcodeScanned} />
              </CardContent>
            </Card>

            {/* Manual Selection & Details */}
            <Card>
              <CardHeader>
                <CardTitle>Datos de Salida</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Artículo</Label>
                    <div className="relative">
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                          value={selectedArticulo ? `${selectedArticulo.codigo} - ${selectedArticulo.nombre}` : articuloSearch}
                          onChange={(e) => {
                            setArticuloSearch(e.target.value);
                            setSelectedArticulo(null);
                            setShowArticulosList(true);
                          }}
                          onFocus={() => setShowArticulosList(true)}
                          placeholder="Buscar por código o nombre..."
                          className="pl-10 pr-10"
                        />
                        {(selectedArticulo || articuloSearch) && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedArticulo(null);
                              setArticuloSearch("");
                              setShowArticulosList(false);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      {showArticulosList && !selectedArticulo && (
                        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {filteredArticulos.length === 0 ? (
                            <div className="p-3 text-center text-slate-500">No se encontraron artículos</div>
                          ) : (
                            filteredArticulos.slice(0, 50).map(art => (
                              <button
                                key={art.id}
                                type="button"
                                onClick={() => handleSelectArticulo(art.id)}
                                className="w-full px-3 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 border-b border-slate-100 dark:border-slate-700 last:border-0"
                              >
                                <span className="font-mono text-xs text-slate-500 bg-slate-100 dark:bg-slate-600 px-2 py-0.5 rounded">{art.codigo}</span>
                                <span className="flex-1 truncate">{art.nombre}</span>
                                <span className={`text-xs ${art.stock_actual <= art.stock_minimo ? "text-red-500" : "text-slate-400"}`}>{art.stock_actual} disp.</span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    {selectedArticulo && (
                      <div className={`p-3 rounded-lg border ${selectedArticulo.stock_actual <= selectedArticulo.stock_minimo ? "bg-red-50 border-red-200" : "bg-orange-50 border-orange-200"}`}>
                        <div className="flex justify-between">
                          <div>
                            <p className="font-medium text-slate-800">{selectedArticulo.nombre}</p>
                            <p className="text-sm text-slate-600 font-mono">{selectedArticulo.codigo}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-slate-600">Stock disponible</p>
                            <p className={`font-bold ${selectedArticulo.stock_actual <= selectedArticulo.stock_minimo ? "text-red-700" : "text-orange-800"}`}>
                              {selectedArticulo.stock_actual} {selectedArticulo.unidad_medida}
                            </p>
                            {selectedArticulo.stock_actual <= selectedArticulo.stock_minimo && (
                              <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                                <AlertTriangle className="w-3 h-3" />
                                Stock bajo
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Cantidad *</Label>
                    <Input
                      type="number"
                      value={formData.cantidad}
                      onChange={(e) => setFormData({ ...formData, cantidad: parseFloat(e.target.value) || 0 })}
                      min={0.01}
                      max={selectedArticulo?.stock_actual || 9999}
                      step={1}
                    />
                    {selectedArticulo && formData.cantidad > selectedArticulo.stock_actual && (
                      <p className="text-xs text-red-500">Excede el stock disponible</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Precio Unitario ($)</Label>
                    <Input
                      type="number"
                      value={formData.precio_unitario}
                      onChange={(e) => setFormData({ ...formData, precio_unitario: parseFloat(e.target.value) || 0 })}
                      min={0}
                      step={0.01}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Motivo</Label>
                    <Select value={formData.motivo} onValueChange={(v) => setFormData({ ...formData, motivo: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MOTIVOS_SALIDA.map(m => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center justify-between">
                      <span>Cliente / Referencia</span>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant={referenciaMode === "usuario" ? "default" : "ghost"}
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => setReferenciaMode("usuario")}
                        >
                          <Users className="w-3 h-3 mr-1" />
                          Usuario
                        </Button>
                        <Button
                          type="button"
                          variant={referenciaMode === "manual" ? "default" : "ghost"}
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => setReferenciaMode("manual")}
                        >
                          Manual
                        </Button>
                      </div>
                    </Label>
                    {referenciaMode === "usuario" ? (
                      <Select 
                        value={formData.referencia_usuario_id || "none"} 
                        onValueChange={(v) => {
                          const selectedValue = v === "none" ? "" : v;
                          const usuario = usuarios.find(u => u.id === selectedValue);
                          setFormData({ 
                            ...formData, 
                            referencia_usuario_id: selectedValue,
                            referencia: usuario ? usuario.name : ""
                          });
                        }}
                      >
                        <SelectTrigger>
                          <User className="w-4 h-4 mr-2 text-slate-400" />
                          <SelectValue placeholder="Seleccionar usuario..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin asignar</SelectItem>
                          {usuarios.map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              <div className="flex items-center gap-2">
                                <span>{u.name}</span>
                                <span className="text-xs text-slate-400">{u.email}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={formData.referencia}
                        onChange={(e) => setFormData({ ...formData, referencia: e.target.value, referencia_usuario_id: "" })}
                        placeholder="Nombre del cliente o # pedido"
                      />
                    )}
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Observaciones</Label>
                    <Textarea
                      value={formData.observaciones}
                      onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                      placeholder="Notas adicionales..."
                      rows={2}
                    />
                  </div>
                </div>
                <Button 
                  onClick={addToList} 
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  disabled={!selectedArticulo || formData.cantidad <= 0 || (selectedArticulo && formData.cantidad > selectedArticulo.stock_actual)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar a la Lista
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Items List */}
          <div className="space-y-6">
            <Card className="border-orange-200">
              <CardHeader className="bg-orange-50 rounded-t-lg">
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" />
                    Lista de Salidas
                  </span>
                  <Badge variant="secondary">{items.length} items</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {items.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Package className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p>Agrega artículos a la lista</p>
                  </div>
                ) : (
                  <ScrollArea className="h-64">
                    <div className="space-y-2">
                      {items.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{item.articulo?.nombre}</p>
                            <p className="text-xs text-slate-500 font-mono">{item.articulo?.codigo}</p>
                          </div>
                          <div className="text-right mr-2">
                            <Badge className="bg-orange-100 text-orange-700">-{item.cantidad}</Badge>
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => removeFromList(index)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}

                {items.length > 0 && (
                  <div className="mt-4 pt-4 border-t space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Total unidades:</span>
                      <span className="font-bold">{totalItems}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Valor total:</span>
                      <span className="font-bold text-orange-600">
                        ${totalValue.toLocaleString("es-PA", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <Button 
                      onClick={handleSubmit} 
                      className="w-full bg-orange-600 hover:bg-orange-700"
                      disabled={saving}
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Registrar {items.length} Salida{items.length > 1 ? "s" : ""}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
